import { MenuItemDef } from '@ag-grid-community/core'
import { SourceKey } from '../models/source-key.model'
import {
  CreateMenuItem,
  TranCategoryKey,
} from '../models/transaction-metadata.model'
import { WorkbenchRow } from '../models/workbench-row.model'
import { MatchingService } from '../services/matching.service'
import { NOT_PERMITTED_REASON, someRowAllowsAction } from './available-actions'

export interface ContextMenuDeps {
  sourceKey: SourceKey
  matching: MatchingService
  /** The right-clicked rows — this grid only. Scope of Unmatch and of the Ignore items. */
  scopeRows: WorkbenchRow[]
  /**
   * Every row selected across BOTH grids — the scope a Match would submit. Match is inherently
   * cross-grid, so its pre-flight checks cannot be answered from {@link scopeRows} alone.
   */
  selectionRows: WorkbenchRow[]
  onMatch: () => void
  onUnmatch: () => void
  /** Post a real `hold` action for {@link scopeRows} — External transactions only. */
  onHold: () => void
  /** Post a real `ignore` action for {@link scopeRows} — External transactions only. */
  onIgnore: () => void
  /**
   * Clear the LOCAL `ignored` flag on the given rows. Still local-only: the flex contract has no
   * `unignore` verb, so there is nothing to post. See the Unignore item's own comment.
   */
  onUnignore: (rowIds: string[]) => void
  /**
   * Report a posting choice. `tranCategory` is set only for Post Enfusion, whose submenu IS the picker —
   * the Clearwater labels stay placeholders and carry none.
   */
  onPost: (label: string, tranCategory?: TranCategoryKey) => void
  createMenu: readonly CreateMenuItem[]
  onEditTransaction: () => void
  highlightItems: (string | MenuItemDef)[]
  filterOnCell: (string | MenuItemDef)[]
}

type MenuItem = string | MenuItemDef

const iconOf = (iconName?: string): Pick<MenuItemDef, 'icon'> =>
  iconName ? { icon: `<i class="mdi ${iconName}"></i>` } : {}

const disabled = (name: string, iconName?: string): MenuItemDef => ({
  name,
  disabled: true,
  ...iconOf(iconName),
})

/**
 * An item that renders and reads as enabled but does nothing yet.
 *
 * It has to be a `MenuItemDef` rather than a plain label: ag-Grid accepts a bare string ONLY when it
 * names one of its own built-in items (`separator`, `copy`, `export`, …) and silently drops anything
 * else. Every custom label in this menu that was written as a plain string was therefore never
 * reaching the screen — which is exactly why Find in External File Viewer, Create Security Mapping,
 * View Comments and Add Comment were missing from the panel while appearing in the design.
 *
 * `action` is an explicit no-op so the item is clickable and not greyed out. Items that should read
 * as unavailable use {@link disabled} instead.
 */
const placeholder = (name: string, iconName?: string): MenuItemDef => ({
  name,
  ...iconOf(iconName),
  action: () => undefined,
})

/**
 * A Delete item gated on the server's per-row permission.
 *
 * NOTE: the flex actions contract has no delete verb yet (`FlexTransactionActionType` is
 * `match` / `unmatch` only), so `action` stays the placeholder every other unimplemented item in
 * this menu uses. The gating is wired so that the item enables the moment the server reports
 * `delete` — and a handler still has to be added before clicking it does anything.
 */
const deleteItem = (name: string, permitted: boolean): MenuItemDef => ({
  name,
  disabled: !permitted,
  tooltip: permitted ? undefined : NOT_PERMITTED_REASON,
  icon: '<i class="mdi mdi-delete"></i>',
})

/** The Prometheus menu item that posts a matched row into Enfusion (PVAL-26341 entry point 4). */
export const POST_ENFUSION_LABEL = 'Post Enfusion'

/**
 * Post Enfusion's picker, mapped from the document's own Create menu.
 *
 * The tran category cannot be inferred from the scoped row — it is a Prometheus row, so it carries
 * neither `gLEntryId` nor a `tradeType` — which is why this is an explicit choice the user makes,
 * in the same order and with the same labels as the Enfusion Create menu. It used to be a pair of
 * literals here, which meant the picker silently opened a category the document no longer named the
 * moment a key was renamed.
 *
 * Recursive because the menu nests: a `menuGroup` becomes a hover-only flyout whose children carry
 * the categories, and only a leaf gets an `action`.
 */
const postEnfusionPicker = (
  items: readonly CreateMenuItem[],
  onPick: (tranCategory: TranCategoryKey) => void,
): MenuItemDef[] =>
  items.map<MenuItemDef>(item =>
    item.children?.length
      ? { name: item.label, subMenu: postEnfusionPicker(item.children, onPick) }
      : {
          name: item.label,
          action: () => item.tranCategory && onPick(item.tranCategory),
        },
  )

/**
 * Unignore's hint, shown in both states.
 *
 * PVAL-26309 made Ignore a durable server write while Unignore stayed local, so the pair no longer
 * reads as a toggle even though the labels still look like one: this item clears the local
 * `row.ignored` flag and cannot reverse a posted Ignore. The label is deliberately unchanged —
 * Transactions 1.0 uses the same word for the same row state, and renaming it here would invent a
 * vocabulary difference between the two panels — so the meaning is carried in the tooltip. It is
 * unconditional rather than a disabled-reason, because it explains what the item DOES; the
 * `availableActions` items use their tooltip for why they are unavailable, and this one has no such
 * gate to report.
 */
export const UNIGNORE_HINT =
  'Clears the local ignored flag on these rows only. An Ignore posted to the server is not reversed here — reverse it in the Transactions 1.0 External Transactions panel.'

/** Posting types offered under Post Clearwater. */
const POSTING_TYPES = [
  'Post Cash Entry',
  'Post Trade',
  'Post FX Trade',
  'Post Currency Forward',
  'Post Wire Transfer',
  'Post Security Transfer',
]

/**
 * Build the source-specific right-click menu. Match / Match Additional / Unmatch / Hold / Ignore
 * post real actions to `POST /flex-transactions/actions`; Unignore is the one item still on local
 * state, because the flex contract has no `unignore` verb (PVAL-26309); the remaining items
 * reproduce the design's full action set (several are placeholders / disabled seams for flows that
 * live in other panels or are deferred).
 *
 * Match, Unmatch, Delete, Hold and Ignore are enabled or disabled SOLELY by the per-row
 * `availableActions` the query API returns (`utils/available-actions.ts`). There is deliberately no
 * client-side eligibility logic mirroring the server's validation rules: it went stale against
 * reality. A submitted match whose perseus Run then FAILED is still un-landed, so a UI guard that
 * refused to re-submit it left the user with no way to recover — the rows were unmatched, the match
 * was never going to arrive, and the menu item stayed disabled. The server owns those decisions; a
 * rejection comes back as a 400 and is rendered from its `validationId`.
 *
 * NOTE on Hold and Ignore specifically: both write a durable `ExtTranStatus` server-side (helios
 * !498) that Transactions 2.0 cannot yet READ back — the flex projection hardcodes
 * `matchStatus: "UNMATCHED"` — and neither has an inverse here. The state is visible and reversible
 * only in the Transactions 1.0 External Transactions panel, keyed by the same
 * `(extTranId, accountId)` pair. That is disclosed where the user meets it — in
 * `FLEX_ACTION_SUCCESS_NOTE` after the action is accepted, in `FLEX_ACTION_VALIDATION_COPY` when the
 * server rejects it, and in {@link UNIGNORE_HINT} for the one item whose label now overpromises.
 * The item labels themselves are unchanged: they match Transactions 1.0's, and a temporary backend
 * gap should not be baked into permanent chrome.
 */
export function buildContextMenuItems(deps: ContextMenuDeps): MenuItem[] {
  const { scopeRows, matching } = deps
  const rowIds = scopeRows.map(row => row.rowId)
  const cnt = rowIds.length > 1 ? ` ${rowIds.length}` : ''

  // The label flips to "Match Additional" when any right-clicked row is already matched. It is
  // cosmetic only: the server distinguishes FLEX_MATCH from FLEX_ADD_MATCH but resolves which
  // itself, and the request body is identical either way — so there is no second enum member.
  const isMatched = scopeRows.some(row => matching.isMatched(row))
  const matchLabel = isMatched ? 'Match Additional' : 'Match'

  // `availableActions` from the query API is the ONLY thing that enables or disables these items.
  // Match and Unmatch are scoped to `selectionRows` (the selection across BOTH grids plus the
  // right-clicked rows), Delete, Hold and Ignore to this grid's rows.
  const matchPermitted = someRowAllowsAction(deps.selectionRows, 'match')
  const unmatchPermitted = someRowAllowsAction(deps.selectionRows, 'unmatch')
  const deletePermitted = someRowAllowsAction(scopeRows, 'delete')
  const ignorePermitted = someRowAllowsAction(scopeRows, 'ignore')
  const holdPermitted = someRowAllowsAction(scopeRows, 'hold')

  const matchItem: MenuItemDef = {
    name: matchLabel,
    disabled: !matchPermitted,
    tooltip: matchPermitted ? undefined : NOT_PERMITTED_REASON,
    icon: '<i class="mdi mdi-link"></i>',
    action: () => deps.onMatch(),
  }
  const unmatchItem: MenuItemDef = {
    name: `Unmatch${cnt}`,
    disabled: !unmatchPermitted,
    tooltip: unmatchPermitted ? undefined : NOT_PERMITTED_REASON,
    icon: '<i class="mdi mdi-link-off"></i>',
    action: () => deps.onUnmatch(),
  }

  if (deps.sourceKey === 'cw') {
    return [
      placeholder('Clear', 'mdi-check'),
      disabled('Clear & Set Post Date', 'mdi-check-all'),
      disabled('Unclear', 'mdi-close'),
      'separator',
      matchItem,
      disabled('Match Using External Data', 'mdi-link-variant'),
      unmatchItem,
      'separator',
      deleteItem(`Delete${cnt}`, deletePermitted),
      deleteItem(`Delete & Ignore${cnt}`, deletePermitted && ignorePermitted),
      'separator',
      placeholder('Find in External File Viewer', 'mdi-folder-open'),
      ...deps.filterOnCell,
      'separator',
      placeholder('Edit Note', 'mdi-note-outline'),
      disabled('Delete Note', 'mdi-note-remove-outline'),
      'separator',
      placeholder('Edit Transaction', 'mdi-pencil'),
      placeholder('Edit Spec Lots', 'mdi-pencil'),
      placeholder('Edit Type Label', 'mdi-pencil'),
      placeholder('Move to Different Sleeve', 'mdi-arrow-right'),
      'separator',
      ...deps.highlightItems,
      'separator',
      placeholder('View Comments', 'mdi-comment-outline'),
      placeholder('Add Comment', 'mdi-comment-plus-outline'),
    ]
  }

  if (deps.sourceKey === 'api') {
    // Enfusion rows are the ones the metadata-driven modal curates, so Edit Transaction is a real
    // action here (it stays a placeholder on the Clearwater menu, whose entry flow lives in
    // `create-transactions-modal`).
    //
    // One row opens the transaction on its own; several open the Bulk Transactions grid, where the
    // same fields become columns and each row is a transaction. The label and the glyph change with
    // the count so the item says which dialog it is about to open — a plural "Edit Transactions (3)"
    // behind the grid icon, rather than a singular label that quietly opens something else.
    const bulk = scopeRows.length > 1
    const editTransaction: MenuItemDef = {
      name: bulk
        ? `Edit Transactions (${scopeRows.length})`
        : 'Edit Transaction',
      disabled: scopeRows.length === 0,
      icon: `<i class="mdi ${bulk ? 'mdi-table-large' : 'mdi-pencil'}"></i>`,
      action: () => deps.onEditTransaction(),
    }

    return [
      matchItem,
      unmatchItem,
      'separator',
      deleteItem(`Delete${cnt}`, deletePermitted),
      'separator',
      ...deps.filterOnCell,
      'separator',
      placeholder('Edit Note', 'mdi-note-outline'),
      disabled('Delete Note', 'mdi-note-remove-outline'),
      'separator',
      editTransaction,
      'separator',
      ...deps.highlightItems,
    ]
  }

  // Prometheus
  const promRows = scopeRows
  const noneIgnored = promRows.every(row => !row.ignored)

  /**
   * Post Enfusion, over one row or over several.
   *
   * The picker is the same either way and so is the tran category it names: a Prometheus row carries
   * neither `gLEntryId` nor a `tradeType`, so the category is the user's explicit choice rather than
   * something inferred from the selection — which is precisely why posting SEVERAL rows needs no
   * extra decision. One row opens the single dialog on it; several open the Bulk Transactions grid
   * with one row per transaction, all on the category just chosen.
   *
   * This used to be gated to exactly one row (BC-10) with the count in the label, because posting a
   * multi-row selection would otherwise have taken `scopeRows[0]` — an arbitrary pick of which row
   * got posted, with no affordance saying which. The grid removes that ambiguity by showing every
   * selected row, so the gate goes with it and the count moves into the item's own label.
   */
  const postEnfusion: MenuItemDef =
    promRows.length >= 1
      ? {
          name:
            promRows.length > 1
              ? `${POST_ENFUSION_LABEL} (${promRows.length})`
              : POST_ENFUSION_LABEL,
          icon: '<i class="mdi mdi-share"></i>',
          subMenu: postEnfusionPicker(deps.createMenu, tranCategory =>
            deps.onPost(POST_ENFUSION_LABEL, tranCategory),
          ),
        }
      : disabled(`${POST_ENFUSION_LABEL}${cnt}`, 'mdi-share')

  return [
    {
      name: 'Post Clearwater',
      icon: '<i class="mdi mdi-share"></i>',
      subMenu: POSTING_TYPES.map<MenuItemDef>(label => ({
        name: label,
        action: () => deps.onPost(label),
      })),
    },
    postEnfusion,
    'separator',
    matchItem,
    disabled('Match Using External Data', 'mdi-link-variant'),
    unmatchItem,
    'separator',
    {
      // Hold sits above Ignore, matching the Transactions 1.0 External Transactions menu's order
      // (Hold, Unhold, Ignore, Unignore). Placement, label and icon glyph still want a human eye
      // against the ticket's design screenshot — `mdi-pause-circle-outline` is a proposal, not a
      // design source. There is deliberately no Unhold item: the flex contract has no such verb.
      name: `Hold${cnt}`,
      disabled: !holdPermitted,
      tooltip: holdPermitted ? undefined : NOT_PERMITTED_REASON,
      icon: '<i class="mdi mdi-pause-circle-outline"></i>',
      action: () => deps.onHold(),
    },
    {
      // A real server action now (PVAL-26309 / helios !498), not a local flag. It posts
      // `{ actionType: 'ignore', extTranIds }` and the panel shows the outcome through the optimistic
      // status overlay — it does NOT set `row.ignored`, which is why Unignore below stays disabled.
      name: `Ignore${cnt}`,
      disabled: !ignorePermitted,
      tooltip: ignorePermitted ? undefined : NOT_PERMITTED_REASON,
      icon: '<i class="mdi mdi-cancel"></i>',
      action: () => deps.onIgnore(),
    },
    {
      // The one item still on local state, and deliberately left that way. The flex contract reports
      // `ignore` but no `unignore` (!498 ships neither `unhold` nor `unignore`), so there is nothing
      // to post: gating this on the forward permission would strand an already-ignored row with no
      // way back, and dropping the check entirely would leave it permanently enabled.
      //
      // It reads `row.ignored`, which a posted Ignore no longer sets, so in practice it is disabled
      // for rows ignored through the server. That is honest rather than unfortunate: a local undo
      // would not reverse the server write. The recovery path is the Transactions 1.0 External
      // Transactions panel until an `unignore` verb exists — which is what {@link UNIGNORE_HINT}
      // says, since the label alone still reads as Ignore's inverse.
      name: `Unignore${cnt}`,
      disabled: noneIgnored,
      tooltip: UNIGNORE_HINT,
      icon: '<i class="mdi mdi-cancel"></i>',
      action: () => deps.onUnignore(rowIds),
    },
    'separator',
    placeholder('Find in External File Viewer', 'mdi-folder-open'),
    ...deps.filterOnCell,
    'separator',
    placeholder('Create Security Mapping', 'mdi-sitemap'),
    'separator',
    ...deps.highlightItems,
    'separator',
    placeholder('View Comments', 'mdi-comment-outline'),
    placeholder('Add Comment', 'mdi-comment-plus-outline'),
  ]
}
